import { QuestionnaireCategoryItemsItemOneOf as Question, Questionnaire } from '@/api/generated-catch-backend/model';
import { useCallback, useEffect, useMemo } from 'react';
import { isQuestion } from '../schemas/questionnaire-schema';
import { DeselectDisplay } from './deselect-display/deselect-display';
import { ErrorDisplay } from './error-display/error-display';
import { QuestionnaireCard } from './questionnaire-card/questionnaire-card';
import { Header } from './questionnaire-header/header';
import { QuestionnaireWrapper } from './questionnaire-wrapper';
import { SkipDisplay } from './skip-display/skip-display';
import { NextButton } from './survey-item/components/next-button/button';
import { SurveyItem } from './survey-item/survey-item';

// Context and hooks
import { QuestionnaireProvider, useQuestionnaireContext } from '../context/QuestionnaireContext';
import { useQuestionnaireForm } from '../hooks/use-questionnaire-form';
import { useQuestionnaireLoading } from '../hooks/use-questionnaire-loading';
import { useQuestionnaireNavigation } from '../hooks/use-questionnaire-navigation';
import { useQuestionnaireSession } from '../hooks/use-questionnaire-session';
import { extractLottieUrls, usePrefetchLotties } from '../utils/lottie-queries';
import { ThreeDotLoader } from './loader/loader';
import { OnNext } from './types';

interface QuestionnaireContentProps {
  questionnaire?: Questionnaire;
  onComplete?: () => void;
}

function QuestionnaireContent({ questionnaire: providedQuestionnaire, onComplete }: QuestionnaireContentProps) {
  const { state, dispatch } = useQuestionnaireContext();

  // Initialize hooks
  const { isLoading } = useQuestionnaireLoading(providedQuestionnaire);
  const {
    selectedQuestionnaire,
    questions,
    questionToCategory,
    getOrCreateResponse,
    hasExistingResponse,
    isRestoringSession,
    responseWithAnswers,
    resetSession,
  } = useQuestionnaireSession(providedQuestionnaire);

  const {
    form,
    resetField,
    handleNext: formHandleNext,
    handleFormSubmit,
    getValues,
    FormProvider,
    isQuestionAnswered,
    loadFormDataFromAnswers,
  } = useQuestionnaireForm(
    getOrCreateResponse,
    async () => {
      await resetSession();
    },
    onComplete,
  );

  const isInitialLoad = (isLoading && !selectedQuestionnaire) || (isLoading && questions.length === 0);

  const { currentQuestionIndex, questionnaireWrapperRef, handleBack, isNavigationBusy } = useQuestionnaireNavigation(
    resetField,
    isQuestionAnswered,
  );

  // Prefetch Lottie animations
  const prefetchLotties = usePrefetchLotties();

  // Current question and answer
  const currentQuestion = questions[currentQuestionIndex];

  // Load form data from backend when responseWithAnswers is available
  useEffect(() => {
    if (responseWithAnswers?.items?.length) {
      loadFormDataFromAnswers(responseWithAnswers.items);
    }
  }, [responseWithAnswers, loadFormDataFromAnswers]);

  useEffect(() => {
    if (currentQuestion) {
      window.scroll({ top: -100, behavior: 'smooth' });
    }
  }, [currentQuestion]);

  useEffect(() => {
    form.clearErrors();
  }, [currentQuestionIndex, form]);

  const memoizedLottieUrls = useMemo(() => extractLottieUrls(state.questions), [state.questions]);
  useEffect(() => {
    if (questions.length > 0) {
      if (memoizedLottieUrls.length > 0) {
        prefetchLotties(memoizedLottieUrls);
      }
    }
  }, [questions, prefetchLotties, memoizedLottieUrls]);

  // Combine navigation and form handling
  const handleNext: OnNext = useCallback(
    (args) => {
      formHandleNext(args);
    },
    [formHandleNext],
  );

  // Listen for email-captured event from email-only questions
  // TODO: Refactor questions to optionally render progress and wrapper so we don't have to do this
  useEffect(() => {
    const handleEmailCaptured = () => {
      handleNext({ skipQuestion: false });
    };

    document.addEventListener('email-captured', handleEmailCaptured);
    return () => {
      document.removeEventListener('email-captured', handleEmailCaptured);
    };
  }, [handleNext]);

  // Category progress calculation
  const currentCategoryIndex = questionToCategory.get(currentQuestionIndex) || 0;
  const totalCategories = selectedQuestionnaire?.categories.length || 0;
  const questionsOnly = questions.filter(isQuestion);

  // Calculate the index within questions-only array
  const currentQuestionOnlyIndex = questions.slice(0, currentQuestionIndex + 1).filter(isQuestion).length - 1;

  if (isInitialLoad) {
    return (
      <div className="flex h-screen w-screen flex-col items-center justify-center bg-black">
        <div className="mb-2 pt-4 text-lg text-white">Loading your personalized risk assessment</div>
        <ThreeDotLoader />
      </div>
    );
  }

  if (isRestoringSession) {
    return (
      <div className="flex h-screen w-screen flex-col items-center justify-center bg-black">
        <div className="text-lg text-white">
          {isRestoringSession ? 'Restoring your session...' : 'Loading questionnaire...'}
        </div>
        {hasExistingResponse && (
          <div className="align-center mt-2 px-4 text-sm text-gray-300">
            We found your previous progress and are taking you back to where you left off.
          </div>
        )}
      </div>
    );
  }

  if (!selectedQuestionnaire || questions.length === 0) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-black">
        <div className="text-lg text-white">No questions available</div>
      </div>
    );
  }

  // Handle interstitial pages - render full screen without form
  if (currentQuestion && currentQuestion.questionnaireItemKind === 'interstitial') {
    return (
      <QuestionnaireWrapper ref={questionnaireWrapperRef}>
        <div className="h-screen w-full">
          <Header
            currentCategoryIndex={currentCategoryIndex}
            categoryName={selectedQuestionnaire.categories[currentCategoryIndex].name}
            totalCategories={totalCategories}
            currentQuestionIndex={currentQuestionIndex}
            currentQuestionOnlyIndex={currentQuestionOnlyIndex}
            questions={questionsOnly}
            questionToCategory={questionToCategory}
            questionnaire={selectedQuestionnaire}
            onBack={handleBack}
            isBackDisabled={isNavigationBusy}
            isInterstitial={true}
          />
          <SurveyItem
            key={currentQuestion.slug}
            surveyItem={currentQuestion}
            onWhyWeAskExpand={() => dispatch({ type: 'SET_WHY_WE_ASK_EXPANDED', payload: true })}
            hideWhyWeAsk={state.isWhyWeAskExpanded}
            onNext={handleNext}
          />
        </div>
      </QuestionnaireWrapper>
    );
  }

  // Handle email-only questions - render full screen with form context
  if (
    currentQuestion &&
    currentQuestion.questionnaireItemKind === 'question' &&
    currentQuestion.questionType === 'email-only'
  ) {
    return (
      <FormProvider {...form}>
        <form onSubmit={handleFormSubmit}>
          <QuestionnaireWrapper ref={questionnaireWrapperRef}>
            <div className="h-screen w-full">
              <SurveyItem
                key={currentQuestion.slug}
                surveyItem={currentQuestion}
                onWhyWeAskExpand={() => dispatch({ type: 'SET_WHY_WE_ASK_EXPANDED', payload: true })}
                hideWhyWeAsk={state.isWhyWeAskExpanded}
                onNext={handleNext}
              />
            </div>
          </QuestionnaireWrapper>
        </form>
      </FormProvider>
    );
  }

  const currentAnswer = currentQuestion ? getValues(currentQuestion.slug) : undefined;

  return (
    <FormProvider {...form}>
      <form onSubmit={handleFormSubmit}>
        <QuestionnaireWrapper
          ref={questionnaireWrapperRef}
          className="questionnaire-wrapper inset-0 h-screen overflow-hidden"
        >
          {/* Header section - 21% of screen height */}
          <Header
            currentCategoryIndex={currentCategoryIndex}
            categoryName={selectedQuestionnaire.categories[currentCategoryIndex].name}
            totalCategories={totalCategories}
            currentQuestionIndex={currentQuestionIndex}
            currentQuestionOnlyIndex={currentQuestionOnlyIndex}
            questions={questionsOnly}
            questionToCategory={questionToCategory}
            questionnaire={selectedQuestionnaire}
            onBack={handleBack}
            isBackDisabled={isNavigationBusy}
          />

          {/* Card section - 79% of screen height */}
          <QuestionnaireCard
            isWhyWeAskExpanded={state.isWhyWeAskExpanded}
            setIsWhyWeAskExpanded={(expanded: boolean) =>
              dispatch({ type: 'SET_WHY_WE_ASK_EXPANDED', payload: expanded })
            }
            currentQuestion={currentQuestion as Question}
            handleNext={handleNext}
          />

          {/* Fixed bottom controls */}
          {!state.isWhyWeAskExpanded && (
            <div className="fixed-bottom-controls">
              <div className="controls-container">
                <div className="controls-overlay">
                  <ErrorDisplay />
                  <DeselectDisplay />
                  <SkipDisplay onSkip={() => handleNext({ skipQuestion: true })} />
                </div>
                <div className="h-8"></div>
              </div>

              <div className="btn-primary-container">
                <NextButton onNext={() => handleNext({ skipQuestion: false })} />
              </div>
            </div>
          )}
        </QuestionnaireWrapper>
      </form>
    </FormProvider>
  );
}

interface QuestionnaireV2RefPageProps {
  questionnaire?: Questionnaire;
  onComplete?: () => void;
}

export default function QuestionnaireV2RefPage({ questionnaire, onComplete }: QuestionnaireV2RefPageProps) {
  return (
    <QuestionnaireProvider>
      <QuestionnaireContent questionnaire={questionnaire} onComplete={onComplete} />
    </QuestionnaireProvider>
  );
}
